<template>
  <div id="support">

    <main>
      <div class="outer-wrapper">
        <h2>Contrology<sup>®</sup> Reformer Support</h2>

        <support-nav />

        <link-list
          heading="Assembly Instructions"
          ce=true
          :links="instructions"
        />

        <video-link
          heading="Maintenance Videos"
          :videos="videos"
        />

        <section class="accordion-wrapper">
          <p class="heading">Common Questions</p>
          <accordion title="What if I need to replace the upholstery on my Reformer carriage?" animation="reveal">
            <p style="margin-bottom:0; padding-bottom:0">The Contrology Reformer carriage pad was built to last, with premium quality vinyl and dense, resilient foam. If you need to replace the upholstery, we offer 3 options:</p>
            <ol>
              <li><b>Replace the carriage pad.</b> We have both <a href="https://s3.amazonaws.com/s3.pilates.com/resources/instructions/16735_Carriage_Pad_Replacement_for_Contrology.pdf" title="Read the instructions" target="_blank">instructions</a> and a <a href="https://video.pilates.com/programs/how-to-replace-the-upholstery-on-a-contrology-reformer" target="_blank">video</a> that demonstrate this option.</li>
              <li><b>Replace the entire carriage.</b> Please <a href="mailto:info@balancedbody.com" title="Contact us via email">contact us</a> to order.</li>
              <li><b>Purchase vinyl and foam.</b> Please <a href="mailto:info@balancedbody.com" title="Contact us via email">contact us</a> for details.</li>
            </ol>
          </accordion>
          <accordion title="What is the popping noise when working on Contrology Reformer?" animation="reveal">
            <p>This noise comes from the bolt and the bushing rubbing on each other. Our classical collaborators specifically requested that Balanced Body not remove this sound as it is “part of the original authentic Reformer experience.”  The popping noise starts when the carriage rolling wheels begin to move. The noise is less noticeable when the carriage is in motion.</p>
            <p><b>Solution:</b> You may reduce this noise by adding 3-in-1 oil* to the carriage rolling wheels (<a href="#" class="lightbox" @click="showModal2 = true">watch this video</a>). Balanced Body does not add lubrication to the rolling wheels during the assembly of this machine. It is also possible that these loud noises will reduce over time once the wheel is broken in.</p>
            <p><em>*Note:  using any lubricant requires occasional cleaning and re-lubrication. No lubrication requires less maintenance.</em></p>
          </accordion>
          <accordion title="What is the scratching on the shoulder rests?" animation="reveal">
            <p>Scratching on the shoulder rests is caused by dragging the leather straps over the shoulder pads. Scratching is made worse by strap stiffness and pressure.  Contrology leather straps are pre-stretched to minimize stretching. Contrology pre-stretched leather stiffness could damage vinyl more than non-pre-stretched leather. However, they will soften over time, with use.</p>
            <p><b>Solution:</b>  Avoid rubbing straps on the shoulder rests, if possible.  To reduce scratching caused by the leather straps, treat the straps with leather conditioner to soften them (Mink oil works great). This will also help the leather last longer.</p><p><em>*Please note that leather conditioner will darken the leather straps.</em></p>
          </accordion>
          <accordion title="Contrology Springs seem to be too loose. Why? And can they be adjusted?" animation="reveal">
            <p>Contrology Reformers are designed to allow for 7/8” (22mm) of Zero Spring Assist (ZSA) on both carriage return and extension. This means that the springs will go “slack” at the begining of extension and the last bit of return.  When properly adjusted, the outside of the spring hook barely touches the spring bar when the carriage is all the way forward.</p>
            <p><b>Solution:</b> To customize ZSA, you can adjust the spring anchor bar under the carriage (<a href="#" class="lightbox" @click="showModal1 = true">watch this video</a>).</p>
          </accordion>
          <accordion title="Optional Footbar Covers: What type of covers are available and which Reformers do they fit?" animation="reveal">
            <p style="margin-bottom:0; padding-bottom:0">We offer 2 types of optional footbar covers for the Contrology Reformers:</p>
            <ol>
              <li>Unpadded Footbar Cover.  This option provides a stickier surface and a very small amount of softness. It adds very little to the thickness of the footbar.</li>
              <li>Padded Footbar Cover. This includes ¼” (6mm) of foam in addition to the cover.  This option makes the footbar softer and thicker.</li>
            </ol>
            <p>Please refer to the table below to see what Contrology Reformer models may add an optional footbar cover.</p>
            <table>
              <thead>
                <tr>
                  <th>Reformer Model</th>
                  <th>Unpadded Footbar Cover</th>
                  <th>Padded Footbar Cover</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colspan="3">JGRIMES 80”</td>
                </tr>
                <tr>
                  <td>80" Custom</td>
                  <td class="center">X</td>
                  <td class="center">X</td>
                </tr>
                <tr>
                  <td>86" Custom</td>
                  <td class="center">X</td>
                  <td class="center">X</td>
                </tr>
                <tr>
                  <td>89" Custom</td>
                  <td class="center">X</td>
                  <td class="center">X</td>
                </tr>
              </tbody>
            </table>
          </accordion>
          <accordion title="What can I do to maintain my Reformer frame?" animation="reveal">
            <p>Contrology Reformers are built to last generations. Original aluminum Reformers were completely unfinished.  Scratches resulting from manufacturing (and use) were left on the Reformers giving them a rustic look.  Eventually, a more consistent finish became desirable and manufacturers decided that braiding the aluminum would make Reformers look more finished.  Balanced Body is continuing with this braided finish.</p>
            <p><b>Solution:</b> Furniture paste, wax, or polish can be used to clean and refresh your aluminum frame.</p><p><em>Note: Scratches in metal will remain.</em></p>
          </accordion>
          <accordion title="How do I care for and maintain my Contrology leather straps?" animation="reveal">
            <p>Wipe away dust and spots on leather with a soft cloth.  To spot clean leather, use a bit of water and a soft bristled brush to remove any grime or dirt. Then use a soft cloth to wipe off.</p>
            <p>Apply a leather conditioner every 6 months. Leather conditioner is a moisturizer that will be soaked up by the leather and can reduce potential cracking. We recommend mink oil, neatsfoot oil or finding a non-animal leather conditioner produced by a reputable manufacturer. Keep in mind that some conditioners, if over-applied, will darken the leather.</p>
          </accordion>
        </section>

        <p>Didn't find your answer? Please use the Contact Us link in the footer.</p>
      </div>
    </main>

    <Modal v-if="showModal1" @close="showModal1 = false">
      <div slot="header"></div>
      <div class="resp-container" slot="body">
        <iframe title="This is an embedded iframe video" class="resp-iframe" src="https://www.youtube.com/embed/QYDoV_X3Xvw?autoplay=1" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
      </div>
      <div slot="footer">
        How to Adjust the Spring Bracket on a Contrology<sup>®</sup> Reformer
      </div>
    </Modal>

    <Modal v-if="showModal2" @close="showModal2 = false">
      <div slot="header"></div>
      <div class="resp-container" slot="body">
        <iframe title="This is an embedded iframe video" class="resp-iframe" src="https://www.youtube.com/embed/uLgX3D5rrwg?autoplay=1" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
      </div>
      <div slot="footer">
        How to Oil the Wheels on a Contrology<sup>®</sup> Reformer
      </div>
    </Modal>

  </div>
</template>

<script>
  import SupportNav from '@/components/SupportNav.vue';
  import LinkList from '@/components/LinkList.vue';
  import VideoLink from '@/components/VideoLink.vue';
  import Accordion from '@/components/Accordion.vue';
  import Modal from '@/components/Modal.vue';

  export default {
    name: 'supportreformer',

    metaInfo: {
      title: "Contrology Reformer User Guides - Balanced Body",
      titleTemplate: "%s",
      meta: [
        {
          name: "description",
          content: "Get your Contrology Reformer Assembly, Use & Maintenance Guides here so you can get the full benefit of your Pilates Reformer."
        },
        {
          property: "og:image",
          content: "https://s3.amazonaws.com/s3.pilates.com/contrology/contrology-support.jpg"
        }
      ]
    },

    components: {
      SupportNav,
      LinkList,
      VideoLink,
      Accordion,
      Modal
    },

    data() {
      return {
        showModal1: false,
        showModal2: false,
        instructions: [
          {
            title: 'Contrology Reformer User Guide 16526 (PDF)',
            url: 'https://s3.amazonaws.com/s3.pilates.com/resources/16526_Contrology_CE_CompleteManual_web_12-2-21_1.pdf'
          },
          {
            title: 'Contrology Reformer Assembly 17304 (PDF)',
            url: 'https://s3.amazonaws.com/s3.pilates.com/resources/17304_Contrology_CE_AssemblyInstructions_web_1-24-24.pdf'
          },
          {
            title: 'Carriage Pad Replacement Instructions (PDF)',
            url: 'https://s3.amazonaws.com/s3.pilates.com/resources/instructions/16735_Carriage_Pad_Replacement_for_Contrology.pdf'
          },
          {
            title: 'Side Wheel Replacement Instructions (PDF)',
            url: 'https://s3.amazonaws.com/s3.pilates.com/resources/instructions/16533_ContrologySideWheelReplacement_web.pdf'
          },
        ],
        videos: [
          {
            title: 'How to Unbox',
            url: 'https://video.pilates.com/programs/how-to-unbox-a-contrology-reformer'
          },
          {
            title: 'How to Replace the Carriage Pad',
            url: 'https://video.pilates.com/programs/how-to-replace-the-upholstery-on-a-contrology-reformer'
          },
          {
            title: 'How to Adjust the Spring Bracket',
            url: 'https://video.pilates.com/programs/how-to-adjust-the-spring-bracket-on-a-contrology-reformer'
          },
          {
            title: 'How to Oil the Wheels',
            url: 'https://video.pilates.com/programs/how-to-oil-the-wheels-on-a-contrology-reformer'
          },
          {
            title: 'How to Adjust the Straps on the Contrology® Reformer',
            url: 'https://video.pilates.com/programs/contrologyref_strapadjustmp4-18701d'
          },
        ]
      }
    }
  }
  
</script>

<style scoped lang="scss">
.outer-wrapper {
  max-width: 680px;
  margin: 0 auto;
}

.heading {
  font-family: var(--roboto);
  font-size: 1rem;
  line-height: 1.3125;
  font-weight: 700;
  color: var(--black);
  margin-bottom: 30px;
}

.resp-iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.resp-container {
  position: relative;
  overflow: hidden;
  padding-top: 56.25%;
}

.accordion {
  cursor: pointer;
  border: none;
  text-align: left;
  outline: none;
  transition: 0.4s;
  border-top: 2px solid #010101;
  padding: 5px 0;
  position: relative;
  z-index: -1;
}

.accordion p {
  font-family: var(--roboto);
  font-weight: 700;
  font-size: .8125em;
  line-height: 1.308;
  color:  #333;
  max-width: 80%;
}

.accordion:before {
  content: '\02795';
  font-size: 1em;
  color: #333;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.accordion.active:before {
  content: "\2796";
}

.accordion-wrapper:last-of-type {
  border-bottom:  2px solid #010101;
  margin-bottom: 35px;
}

.panel {
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.2s ease-out;
}

.panel p, ol, table, .panel a {
  font-family: var(--roboto);
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.308;
  color:  var(--black);
  max-width: 90%;
}

.panel ol {
  padding-left: 16px;
  padding-bottom: 10px;
}

.panel li {
  margin-bottom: 10px;
}

table {
  border: 1px solid var(--black);
  max-width: 100%;
  width: 100%;
  border-collapse:separate; 
  border-spacing:0 5px;
  margin-bottom: 20px;
}

thead tr th{
  border-bottom: 1px solid var(--black);
  padding: 5px;
}

td {
  width: 33%;
  padding: 5px;
}

.center {
  text-align: center;
}

@media only screen and (min-width: 1024px) {
  h2 {
    margin-bottom: 10px;
  }
}
</style>
